import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { createClient } from "graphql-ws"
import { idToken } from "@/v1-console/auth/auth.instance"
import config from "@/v1-console/config"

// graphql-ws defaults `shouldRetry` to `isLikeCloseEvent`, so any connection
// problem that is not a CloseEvent - a failed handshake after laptop sleep, a
// suspended tab, a VPN/network switch, a deploy cycling the socket endpoint -
// is treated as fatal and reported without a single retry. The notification
// subscription is always on and app-wide, so one such blip killed it for the
// rest of the session and surfaced as an untriageable "Socket closed"
// (V1-CONSOLE-2T2). Retry those transient failures instead of giving up.
const RETRY_WAIT_BASE_MS = 1000
const RETRY_WAIT_MAX_MS = 30_000
const RETRY_JITTER_MS = 1000

// 4403 is deliberately absent from graphql-ws's own fatal list ("might grant
// access out after retry"), so without opting out here an unlimited retry
// budget would reconnect forever against a token the server keeps rejecting.
const FORBIDDEN_CLOSE_CODE = 4403

function getCloseCode(errOrCloseEvent: unknown) {
  if(!errOrCloseEvent || typeof errOrCloseEvent !== "object") return null
  const { code } = errOrCloseEvent as { code?: unknown }
  return typeof code === "number" ? code : null
}

// CloseEvents (normal socket close) are distinguishable from ErrorEvents
// (connection-level failure, e.g. ECONNREFUSED) by the `wasClean` property,
// which only exists on CloseEvent.
function isCloseEvent(errOrCloseEvent: unknown) {
  return Boolean(
    errOrCloseEvent
    && typeof errOrCloseEvent === "object"
    && "wasClean" in errOrCloseEvent
  )
}

// The default backoff doubles without a ceiling (1s * 2^retries), which with a
// large retry budget would stretch to hours. Cap it so a long offline spell
// still reconnects promptly once the network returns.
async function retryWait(retries: number) {
  const backoff = Math.min(RETRY_WAIT_BASE_MS * 2 ** retries, RETRY_WAIT_MAX_MS)
  const jitter = Math.floor(Math.random() * RETRY_JITTER_MS)
  await new Promise((resolve) => setTimeout(resolve, backoff + jitter))
}

// Track whether we've ever established a connection. Non-CloseEvent failures
// (ECONNREFUSED, failed handshake) are only retried after a first successful
// connection. Before then, a non-CloseEvent means the endpoint is genuinely
// unreachable (backend not running in dev, deploy in progress), and retrying
// would spam the browser console without any chance of recovery. CloseEvents
// are always retried - they mean the socket was alive and then closed cleanly.
let hasConnected = false

const wsLink = new GraphQLWsLink(
  createClient({
    url: config.socketUrl,
    connectionParams() {
      return {
        token: idToken
      }
    },
    // `lazy` stays at its default, so this budget only ever burns while a
    // subscription is actually mounted.
    retryAttempts: Infinity,
    shouldRetry(errOrCloseEvent) {
      if(getCloseCode(errOrCloseEvent) === FORBIDDEN_CLOSE_CODE) return false
      // Only retry non-CloseEvent failures if we've connected before. This
      // prevents repeated browser console errors when the backend isn't
      // reachable (local dev without the backend running, unreachable endpoint).
      if(!isCloseEvent(errOrCloseEvent) && !hasConnected) return false
      return true
    },
    on: {
      connected() { hasConnected = true }
    },
    retryWait
  })
)

export default wsLink
